import type { Runtime } from './runtime';
import { optimisticAppointment } from './runtime';
import { deviceT } from './messages';
import type { T } from './messages';
import type { OutboxEntry } from './outbox/entry';
import type { DayHours } from '@yourstore/domain';
import type { WireIdentity } from './wire';

/**
 * Every write the staff can make, as a function that appends one intent.
 *
 * **This is the entire write surface of the app.** No view calls `api.request`
 * and no view calls `mirror.put*`. A tap produces an intent; the outbox does the
 * rest. Spec §8.2 describes that as the offline behaviour, but building it as
 * the *only* behaviour is what makes it trustworthy — the path that runs in
 * airplane mode is the path that ran at the counter this morning with four bars
 * of signal.
 *
 * Each function's job is small and identical in shape: name the intent, carry
 * the request fields `core-api`'s wire schema expects, and snapshot the human
 * facts the task list would need if this one comes back rejected.
 *
 * ## `describe.what` is a snapshot, and snapshots do not get re-translated
 *
 * Every `what` below is written through `t` and is therefore in whatever
 * language the person who tapped the button was working in. It is then
 * **persisted in IndexedDB alongside the intent**, and the Tasks list reads it
 * back days later exactly as it was stored.
 *
 * That is deliberate, and it is worth being honest about rather than treating as
 * a limitation. `describe` is not a label for a row — it is *the record of what
 * somebody did*, taken at the moment they did it. Re-rendering an old queued
 * intent through today's catalog would mean a device that changed language
 * silently rewriting the history of the shop's own actions; two staff members
 * sharing an iPad would see each other's queued work described in a language
 * neither of them chose it in. A frozen sentence is the honest one.
 *
 * The consequence is small and bounded: a queue drains in seconds when there is
 * signal, and the only entries old enough for this to be visible are ones that
 * were rejected and are sitting in the Tasks list waiting for a decision.
 */

export interface BookInput {
  serviceId: string;
  serviceName: string;
  /**
   * The room the sheet chose beside the person (0056), for a service that
   * needs both. Sent as `also_resource_ids`; absent on every service that
   * needs one lane, so that request is byte for byte what it always was.
   */
  alsoResourceIds?: readonly string[] | undefined;
  /** What the optimistic row shows — the length this booking will actually be. */
  durationMin: number;
  /**
   * **This booking's own length** (A1), when the owner typed one that is not
   * what the resolver would have picked.
   *
   * Absent means "the server decides", which is the ordinary case and is exactly
   * what this request has always meant. Present is the one thing a staff body
   * may say about a span that no other surface may — see
   * `CreateAppointmentBody` for why the counter is the exception.
   */
  explicitDurationMin?: number | undefined;
  /** Turn that length into *hers*, in the same transaction. Needs a `clientId`. */
  rememberDuration?: boolean | undefined;
  /**
   * **Which way this service is being sold** (migration 0048) — an
   * `item_options` id, and never a price or a span.
   *
   * Present whenever the service is sold in more than one way, including when
   * nobody touched a chip: the sheet resolves the default the same way the
   * engine does, and saying so explicitly is what makes the queued optimistic
   * row and the row the server writes describe the same sale. Absent is a
   * service sold one way, and its request is byte for byte the one this app has
   * always sent.
   */
  optionId?: string | undefined;
  /** What was attached, by catalog id. Each becomes its own line on the book. */
  addOnItemIds?: readonly string[] | undefined;
  /**
   * What the *menu* comes to for this ask — the option's price plus every
   * attached price.
   *
   * **For the optimistic row only.** It is deliberately not spread into the
   * payload below and there is a test that asserts no queued booking ever
   * carries a price (B7): the server prices from the ids, and a device that
   * could name an amount would be a device that could name the wrong one. What
   * it buys is a Today list and a till that read the truth while the intent is
   * queued, instead of `$0`.
   */
  catalogPriceCents?: number | undefined;
  /** The chosen option's name, for the same reason and with the same rule. */
  optionName?: string | undefined;
  /** The attached lines, likewise — shown while queued, never sent. */
  addOns?:
    | readonly {
        service_id: string;
        name: string;
        price_cents: number;
        duration_min: number;
      }[]
    | undefined;
  resourceId: string;
  resourceName: string | null;
  startIso: string;
  partySize?: number | undefined;
  notes?: string | undefined;
  source: 'staff' | 'walk_in';
  /** Somebody already in the book… */
  clientId?: string | undefined;
  /** …or the person standing at the counter. Exactly one of the two. */
  guest?: { name: string; identities: WireIdentity[]; notes?: string | undefined } | undefined;
  clientName: string;
}

/**
 * **The device-side echo of "a support session may look and not touch."**
 *
 * `core-api` refuses every write under the console's read-only look
 * (`reason: support_session`), so a queued intent would only ever become a
 * rejected task on somebody else's screen — the owner's, when she next opens
 * her own iPad, if the outbox were ever shared. It is not, but the honest
 * shape is the one the spec keeps everywhere: **no write intent leaves the
 * device under a support session** (plan, sixth pass X5). Every `actions.*`
 * below appends through this one `enqueue`, so refusing here refuses all of
 * them in one place, before an optimistic row could be drawn.
 */
export class SupportSessionRefusal extends Error {
  readonly reason = 'support_session' as const;
  constructor(message: string) {
    super(message);
    this.name = 'SupportSessionRefusal';
  }
}

function writeSurface(runtime: Runtime, t: T): Pick<Runtime['outbox'], 'enqueue'> {
  return {
    enqueue: (input) => {
      if (runtime.supportSession() !== null) {
        return Promise.reject(new SupportSessionRefusal(t('actions.supportRefused')));
      }
      return runtime.outbox.enqueue(input);
    },
  };
}

export function createActions(runtime: Runtime, now: () => number = Date.now, t: T = deviceT) {
  const outbox = writeSurface(runtime, t);
  const { mirror } = runtime;

  return {
    async book(input: BookInput): Promise<OutboxEntry> {
      const session = await mirror.session();
      // No `price_cents` and no `end`. The server looks the price up from
      // `service_id` (obligation B7) and computes the end itself, and an
      // offline device is the last thing that should be asserting what
      // something costs.
      //
      // `duration_min` is the one exception, added by A1, and it is not a hole
      // in that rule: it is the owner saying how long *she* will be in the room,
      // it is bounded 5..480 by the same schema the database CHECKs, and the
      // price is untouched by it. The server still resolves the final span
      // through `effectiveDurationMin`.
      const payload: Record<string, unknown> = {
        service_id: input.serviceId,
        resource_id: input.resourceId,
        ...(input.alsoResourceIds === undefined || input.alsoResourceIds.length === 0
          ? {}
          : { also_resource_ids: [...input.alsoResourceIds] }),
        start: input.startIso,
        source: input.source,
        ...(input.explicitDurationMin === undefined
          ? {}
          : { duration_min: input.explicitDurationMin }),
        ...(input.rememberDuration === true ? { remember_duration: true } : {}),
        // **Ids, never numbers** (migration 0048, design §8.3). `option_id`
        // buys the option's price and span server-side and `add_on_item_ids`
        // buys each attached price and each attached minute; there is no
        // parameter for either amount and B7 is untouched by both. Both absent
        // for a service sold one way with nothing attached, which keeps that
        // request identical to what an older `core-api` already answers.
        ...(input.optionId === undefined ? {} : { option_id: input.optionId }),
        ...(input.addOnItemIds === undefined || input.addOnItemIds.length === 0
          ? {}
          : { add_on_item_ids: [...input.addOnItemIds] }),
        ...(input.partySize === undefined ? {} : { party_size: input.partySize }),
        ...(input.notes === undefined ? {} : { notes: input.notes }),
        ...(input.clientId === undefined ? {} : { client_id: input.clientId }),
        ...(input.guest === undefined ? {} : { guest: input.guest }),
        ...(session === null ? {} : { tenant_id: session.tenant.tenant_id }),
      };

      return outbox.enqueue({
        kind: 'appointment.create',
        payload,
        describe: {
          what: t('action.book', { client: input.clientName, service: input.serviceName }),
          person: input.clientName,
          whenIso: input.startIso,
        },
        optimistic: optimisticAppointment({
          serviceId: input.serviceId,
          serviceName: input.serviceName,
          resourceId: input.resourceId,
          resourceName: input.resourceName,
          ...(input.alsoResourceIds === undefined
            ? {}
            : { alsoResourceIds: input.alsoResourceIds }),
          startIso: input.startIso,
          durationMin: input.durationMin,
          clientId: input.clientId ?? null,
          clientName: input.clientName,
          source: input.source,
          ...(input.partySize === undefined ? {} : { partySize: input.partySize }),
          // The catalog's numbers, so the queued row reads as the sale it is
          // (0048). They never reach the payload above.
          ...(input.catalogPriceCents === undefined ? {} : { priceCents: input.catalogPriceCents }),
          optionId: input.optionId ?? null,
          optionName: input.optionName ?? null,
          addOns: input.addOns ?? [],
          notes: input.notes,
          nowIso: new Date(now()).toISOString(),
        }),
      });
    },

    async reschedule(input: {
      appointmentId: string;
      startIso: string;
      resourceId?: string | undefined;
      clientName: string;
      serviceName: string;
    }): Promise<OutboxEntry> {
      return outbox.enqueue({
        kind: 'appointment.reschedule',
        target: { appointment_id: input.appointmentId },
        payload: {
          start: input.startIso,
          ...(input.resourceId === undefined ? {} : { resource_id: input.resourceId }),
        },
        describe: {
          what: t('action.reschedule', { client: input.clientName, service: input.serviceName }),
          person: input.clientName,
          whenIso: input.startIso,
        },
      });
    },

    /**
     * **"This one ran long"** (S1) — how long *this booking* actually was.
     *
     * Deliberately not `reschedule` with an extra field: a move keeps the span
     * it was booked at, on every door, and the calendar's drag reads more
     * honestly for it — the body moves a block, the bottom edge changes a
     * length, and the two intents cannot be confused in a task list either.
     *
     * No price and no start. The start is the row's own, the server re-derives
     * the house gap around the new span (A2), and the price came from the
     * catalog when the booking was made (B7) — a treatment that ran over is not
     * a treatment that costs more. Unless the new length is one of the
     * service's own: then it is that option, at that option's catalog price.
     */
    async setAppointmentLength(input: {
      appointmentId: string;
      durationMin: number;
      /**
       * **The option this length is**, when the service is sold at several
       * and the calendar snapped to one of her lengths — the 120 shortened to
       * an hour is the 60, and the till must say so. An id, never a price: the
       * server reads the option's price from the catalog (B7). Absent for a
       * custom length, which keeps the option the booking has (A1).
       */
      optionId?: string | undefined;
      clientName: string;
      serviceName: string;
      whenIso: string;
    }): Promise<OutboxEntry> {
      return outbox.enqueue({
        kind: 'appointment.duration',
        target: { appointment_id: input.appointmentId },
        // In the payload, so a change queued offline is complete on its own.
        payload: {
          duration_min: input.durationMin,
          ...(input.optionId === undefined ? {} : { option_id: input.optionId }),
        },
        describe: {
          what: t('action.retime', {
            client: input.clientName,
            service: input.serviceName,
            minutes: input.durationMin,
          }),
          person: input.clientName,
          whenIso: input.whenIso,
        },
      });
    },

    async cancel(input: {
      appointmentId: string;
      clientName: string;
      whenIso: string;
      feeCents?: number | undefined;
    }): Promise<OutboxEntry> {
      return outbox.enqueue({
        kind: 'appointment.cancel',
        target: { appointment_id: input.appointmentId },
        payload: input.feeCents === undefined ? {} : { fee_cents: input.feeCents },
        describe: {
          what: t('action.cancel', { name: input.clientName }),
          person: input.clientName,
          whenIso: input.whenIso,
        },
      });
    },

    async setStatus(input: {
      appointmentId: string;
      status: 'completed' | 'no_show';
      clientName: string;
      whenIso: string;
    }): Promise<OutboxEntry> {
      return outbox.enqueue({
        kind: 'appointment.status',
        target: { appointment_id: input.appointmentId },
        payload: { status: input.status },
        describe: {
          what:
            input.status === 'completed'
              ? t('action.markDone', { name: input.clientName })
              : t('action.markNoShow', { name: input.clientName }),
          person: input.clientName,
          whenIso: input.whenIso,
        },
      });
    },

    async createClient(input: {
      /**
       * The row's id, minted on this device (`cl_` + ULID, spec §8.2) — which
       * `CreateClientBody` honours as the primary key. Passed when a later
       * intent in the same queue must name this client before the server has
       * answered: the Gift cards sheet adds the recipient here and queues the
       * card behind it, and the outbox's total order lands them in that order.
       */
      id?: string | undefined;
      name: string;
      identities: WireIdentity[];
      notes?: string | undefined;
      tags?: string[] | undefined;
    }): Promise<OutboxEntry> {
      return outbox.enqueue({
        kind: 'client.create',
        payload: {
          ...(input.id === undefined ? {} : { id: input.id }),
          name: input.name,
          identities: input.identities,
          ...(input.notes === undefined ? {} : { notes: input.notes }),
          ...(input.tags === undefined ? {} : { tags: input.tags }),
        },
        describe: { what: t('action.addClient', { name: input.name }), person: input.name },
      });
    },

    async updateClient(input: {
      clientId: string;
      name: string;
      changes: Record<string, unknown>;
    }): Promise<OutboxEntry> {
      return outbox.enqueue({
        kind: 'client.update',
        target: { client_id: input.clientId },
        payload: input.changes,
        describe: { what: t('action.editClient', { name: input.name }), person: input.name },
      });
    },

    async archiveClient(input: {
      clientId: string;
      name: string;
      archived: boolean;
    }): Promise<OutboxEntry> {
      return outbox.enqueue({
        kind: 'client.archive',
        target: { client_id: input.clientId },
        payload: { archived: input.archived },
        describe: {
          what: input.archived
            ? t('action.archiveClient', { name: input.name })
            : t('action.restoreClient', { name: input.name }),
          person: input.name,
        },
      });
    },

    async addIdentity(input: {
      clientId: string;
      name: string;
      identity: WireIdentity;
    }): Promise<OutboxEntry> {
      return outbox.enqueue({
        kind: 'client.identity.add',
        target: { client_id: input.clientId },
        payload: { ...input.identity },
        describe: { what: t('action.addContact', { name: input.name }), person: input.name },
      });
    },

    async removeIdentity(input: {
      clientId: string;
      name: string;
      identity: WireIdentity;
    }): Promise<OutboxEntry> {
      return outbox.enqueue({
        kind: 'client.identity.remove',
        target: { client_id: input.clientId },
        payload: { ...input.identity },
        describe: { what: t('action.removeContact', { name: input.name }), person: input.name },
      });
    },

    async addPreference(input: {
      clientId: string;
      name: string;
      category: 'preference' | 'allergy';
      label: string;
      detail?: string | undefined;
      internal: boolean;
    }): Promise<OutboxEntry> {
      return outbox.enqueue({
        kind: 'preference.create',
        target: { client_id: input.clientId },
        payload: {
          category: input.category,
          label: input.label,
          internal: input.internal,
          ...(input.detail === undefined ? {} : { detail: input.detail }),
        },
        describe: {
          what: t('action.notePreference', { label: input.label, name: input.name }),
          person: input.name,
        },
      });
    },

    /* ---------------------------------------------------------------- */
    /* A1 — her lengths (docs/lovespa-discovery.md §2.1)                 */
    /* ---------------------------------------------------------------- */

    /**
     * *"Susie's takes me 35 minutes."*
     *
     * A `PUT` of one number at one key, so it queues and replays like anything
     * else: the owner can retime a client standing in her doorway with no
     * signal, and the next drain settles it. The screen reads the queued intent
     * meanwhile, which is the Today screen's optimistic-row rule applied to a
     * fact rather than to a booking.
     *
     * `optionId` names **one length** of the service (0048): "105 on the
     * ninety" is a different row from "35 however it is sold", and the desk
     * states each on its own door. It rides the intent's *target*, because it
     * is part of which row this is about and not part of what is said about it
     * — `intents.ts` puts it in the path, where the server reads it.
     */
    async setDuration(input: {
      clientId: string;
      clientName: string;
      serviceId: string;
      serviceName: string;
      optionId?: string | undefined;
      durationMin: number;
    }): Promise<OutboxEntry> {
      return outbox.enqueue({
        kind: 'duration.set',
        target: durationTarget(input),
        payload: { duration_min: input.durationMin },
        describe: {
          what: t('action.rememberDuration', {
            minutes: input.durationMin,
            name: input.clientName,
            service: input.serviceName,
          }),
          person: input.clientName,
        },
      });
    },

    /**
     * "She's back to the normal length." With `optionId`, back to the normal
     * length *of that one length* — the broad row, if she has one, stays.
     */
    async clearDuration(input: {
      clientId: string;
      clientName: string;
      serviceId: string;
      serviceName: string;
      optionId?: string | undefined;
    }): Promise<OutboxEntry> {
      return outbox.enqueue({
        kind: 'duration.clear',
        target: durationTarget(input),
        payload: {},
        describe: {
          what: t('action.forgetDuration', {
            name: input.clientName,
            service: input.serviceName,
          }),
          person: input.clientName,
        },
      });
    },

    async deletePreference(input: {
      clientId: string;
      preferenceId: string;
      name: string;
      label: string;
    }): Promise<OutboxEntry> {
      return outbox.enqueue({
        kind: 'preference.delete',
        target: { client_id: input.clientId, preference_id: input.preferenceId },
        payload: {},
        describe: {
          what: t('action.removePreference', { label: input.label, name: input.name }),
          person: input.name,
        },
      });
    },

    async createService(input: {
      name: string;
      priceCents: number;
      durationMin: number;
      resourceIds: string[];
      description?: string | undefined;
    }): Promise<OutboxEntry> {
      return outbox.enqueue({
        kind: 'service.create',
        payload: {
          name: input.name,
          price_cents: input.priceCents,
          duration_min: input.durationMin,
          resource_ids: input.resourceIds,
          ...(input.description === undefined ? {} : { description: input.description }),
        },
        describe: { what: t('action.addService', { name: input.name }) },
      });
    },

    async updateService(input: {
      serviceId: string;
      name: string;
      changes: Record<string, unknown>;
    }): Promise<OutboxEntry> {
      return outbox.enqueue({
        kind: 'service.update',
        target: { service_id: input.serviceId },
        payload: input.changes,
        describe: { what: t('action.editService', { name: input.name }) },
      });
    },

    /* ---------------------------------------------------------------- */
    /* S2 — the team: people, rooms, their weeks, their time off          */
    /* ---------------------------------------------------------------- */

    /**
     * Add somebody, or a room.
     *
     * One action for both because they are one row — `resources`, with a
     * `kind` — and a pair of functions here would be two spellings of one
     * write that would eventually take two different code paths. The Team
     * screen is what knows they read differently to an owner.
     */
    async createResource(input: {
      name: string;
      kind: 'person' | 'room' | 'equipment';
      sortOrder?: number | undefined;
    }): Promise<OutboxEntry> {
      return outbox.enqueue({
        kind: 'resource.create',
        payload: {
          name: input.name,
          kind: input.kind,
          ...(input.sortOrder === undefined ? {} : { sort_order: input.sortOrder }),
        },
        describe: {
          what:
            input.kind === 'person'
              ? t('action.addPerson', { name: input.name })
              : t('action.addRoom', { name: input.name }),
        },
      });
    },

    /**
     * Rename, retire, reorder, re-week, or link to a login.
     *
     * `changes` is the diff and nothing else, exactly as `updateService`'s is:
     * `UpdateResourceBody` is a partial and the server treats `undefined` as
     * "leave it alone", so a form that always sent every field would turn "I
     * fixed a typo" into a write that also re-asserted a week somebody edited
     * on the other iPad five minutes ago. That matters more here than it does
     * for a service, because `staff_user_id` has **three** states: a string
     * links, `null` unlinks, and absent must stay absent.
     */
    async updateResource(input: {
      resourceId: string;
      name: string;
      changes: Record<string, unknown>;
    }): Promise<OutboxEntry> {
      return outbox.enqueue({
        kind: 'resource.update',
        target: { resource_id: input.resourceId },
        payload: input.changes,
        describe: { what: t('action.editPerson', { name: input.name }) },
      });
    },

    /**
     * Time off, which is `createBlackout` with a `resource_id` on it.
     *
     * Deliberately **not** a second intent kind: a closure and somebody's
     * afternoon off are the same row in the same table, and the only difference
     * is whether `resource_id` is null. Two intents would be two things to keep
     * in step with one `blackouts` table, and the day they drifted the calendar
     * would close for a reason nobody could find.
     */
    async createTimeOff(input: {
      resourceId: string;
      personName: string;
      startIso: string;
      endIso: string;
      reason?: string | undefined;
    }): Promise<OutboxEntry> {
      return outbox.enqueue({
        kind: 'blackout.create',
        payload: {
          start: input.startIso,
          end: input.endIso,
          resource_id: input.resourceId,
          ...(input.reason === undefined ? {} : { reason: input.reason }),
        },
        describe: {
          what: t('action.timeOff', { name: input.personName }),
          person: input.personName,
          whenIso: input.startIso,
        },
      });
    },

    /**
     * Hours are a whole week, always.
     *
     * `PUT /v1/settings/hours` takes all seven days and `catalog.ts` says why:
     * patching one day invites two devices each editing "their" day offline and
     * a merge producing a week neither person chose. Whole-week replacement
     * makes a collision visible as a collision, which is the only outcome §8.3
     * permits.
     */
    async setHours(hours: DayHours[]): Promise<OutboxEntry> {
      return outbox.enqueue({
        kind: 'hours.set',
        payload: { hours },
        describe: { what: t('action.setHours') },
      });
    },

    async createBlackout(input: {
      startIso: string;
      endIso: string;
      resourceId?: string | null;
      reason?: string | undefined;
    }): Promise<OutboxEntry> {
      return outbox.enqueue({
        kind: 'blackout.create',
        payload: {
          start: input.startIso,
          end: input.endIso,
          resource_id: input.resourceId ?? null,
          ...(input.reason === undefined ? {} : { reason: input.reason }),
        },
        describe: {
          what:
            input.reason === undefined
              ? t('action.closeCalendar')
              : t('action.closeFor', { reason: input.reason }),
          whenIso: input.startIso,
        },
      });
    },

    async deleteBlackout(input: {
      blackoutId: string;
      reason: string | null;
    }): Promise<OutboxEntry> {
      return outbox.enqueue({
        kind: 'blackout.delete',
        target: { blackout_id: input.blackoutId },
        payload: {},
        describe: { what: t('action.reopen', { what: input.reason ?? t('action.theCalendar') }) },
      });
    },

    /* ---------------------------------------------------------------- */
    /* MVP 4 — the money (spec §6.5, §7.3, §7.4)                         */
    /* ---------------------------------------------------------------- */

    /**
     * Close out a visit, or ring up a quick sale. One document either way
     * (spec §6.5).
     *
     * **The body names instruments, never amounts that matter.** A line may
     * carry `unit_price_cents` — the staff till is the trusted-actor regime and
     * the owner charges what they charge — but nothing here names a subtotal, a
     * discount total, a total, or which tender pays for what. The waterfall is
     * the server's, its order is fixed in code, and its caps are arithmetic.
     * What the till computed while the owner was typing stays on the till.
     *
     * It queues like every other write, which is the whole point: a checkout in
     * a basement is §8's bread and butter, and this path is the same path the
     * counter uses with four bars of signal.
     */
    async checkout(input: CheckoutInput): Promise<OutboxEntry> {
      const session = await mirror.session();
      const payload: Record<string, unknown> = {
        lines: input.lines.map((line) => ({
          item_id: line.itemId,
          qty: line.qty,
          // Omitted means "the catalog's", which is what an untouched price
          // field means. Sending the list price back would record a deliberate
          // override of zero, and the two are different facts.
          ...(line.unitPriceCents === undefined ? {} : { unit_price_cents: line.unitPriceCents }),
          // "coral scarf" (B4). Trimmed away when empty, for the reason above:
          // an empty string is a note the owner did not write.
          ...(line.note === undefined || line.note === '' ? {} : { note: line.note }),
          // **Which catalog number is this line's list** (migration 0048) —
          // an option id, or the parent an add-on hung off. Both are ids and
          // neither is an amount, so B7 is untouched; what they buy is a
          // server-side list price that matches the one the till showed,
          // rather than the item's headline. Absent on every line of every
          // sale that has no options in it, which keeps that request byte for
          // byte what it was.
          ...(line.optionId === undefined ? {} : { option_id: line.optionId }),
          ...(line.addOnOf === undefined ? {} : { add_on_of: line.addOnOf }),
        })),
        tender: input.tender,
        ...(input.tenderLabel === undefined ? {} : { tender_label: input.tenderLabel }),
        // **The split** (0046). Sent only when there is one, so a single-tender
        // sale is byte for byte the request it has always been — which is what
        // lets an older `core-api` keep answering this device.
        ...(input.tenders === undefined || input.tenders.length === 0
          ? {}
          : {
              tenders: input.tenders.map((part) => ({
                kind: part.kind,
                ...(part.amountCents === undefined ? {} : { amount_cents: part.amountCents }),
                ...(part.label === undefined || part.label === '' ? {} : { label: part.label }),
              })),
            }),
        // A tip, when the owner turned the field on and somebody typed in it.
        ...(input.tipCents === undefined || input.tipCents === 0
          ? {}
          : { tip_cents: input.tipCents }),
        ...(input.appointmentId === undefined ? {} : { appointment_id: input.appointmentId }),
        ...(input.clientId === undefined ? {} : { client_id: input.clientId }),
        ...(input.taxCents === undefined ? {} : { tax_cents: input.taxCents }),
        ...(input.discountCents === undefined ? {} : { discount_cents: input.discountCents }),
        ...(input.certificateCode === undefined ? {} : { certificate_code: input.certificateCode }),
        ...(input.certificateCents === undefined
          ? {}
          : { certificate_cents: input.certificateCents }),
        ...(input.useDeposit === undefined ? {} : { use_deposit: input.useDeposit }),
        ...(input.depositCents === undefined ? {} : { deposit_cents: input.depositCents }),
        ...(input.useCredit === undefined ? {} : { use_credit: input.useCredit }),
        ...(input.creditGiftCents === undefined
          ? {}
          : { credit_gift_cents: input.creditGiftCents }),
        ...(input.creditCashCents === undefined
          ? {}
          : { credit_cash_cents: input.creditCashCents }),
        ...(input.note === undefined ? {} : { note: input.note }),
        ...(session === null ? {} : { tenant_id: session.tenant.tenant_id }),
      };

      return outbox.enqueue({
        kind: 'settlement.create',
        ...(input.appointmentId === undefined
          ? {}
          : { target: { appointment_id: input.appointmentId } }),
        payload,
        describe: {
          what:
            input.appointmentId === undefined
              ? t('action.quickSale', { name: input.clientName ?? t('action.walkIn') })
              : t('action.checkout', { name: input.clientName ?? t('action.guest') }),
          person: input.clientName,
        },
      });
    },

    /**
     * Issue a gift certificate — a **money event in its own right** (§7.4 rule
     * 1: income on `issued_on`, liability outstanding the same instant), never a
     * settlement.
     *
     * **The code is minted here** (`mintCertificateCode`, the server's own
     * alphabet and length, from Web Crypto) or is the number printed on a
     * physical card, and is sent as `code` — so the code the owner reads to
     * the buyer off the confirmation, with or without signal, is the code the
     * server stores. The server uppercases it, validates it and refuses a
     * duplicate with a `409`, which lands as a task like any refusal; at 30¹²
     * a minted collision is not a thing that happens, and a printed one is the
     * owner typing a card twice, which is exactly what the refusal is for. The
     * id is minted here too (`ct_` + ULID) so the confirmation can find the
     * row the drain writes into the mirror.
     *
     * **Who holds it** is `recipient_client_id` (migration 0074) — a client,
     * possibly one queued a moment earlier by `createClient` with its own
     * minted id. The server writes the name snapshot from that client, so no
     * `recipient_name` is sent beside it.
     *
     * **How it was paid** rides along as `tender` (+ `tender_label` for an
     * `other`), migration 0070 — the settlement body's own words. It is a
     * recorded fact about the sale, never a price: the value is still the
     * typed amount or the server's catalog snapshot, exactly as before.
     */
    async issueCertificate(input: {
      /** `ct_` + ULID, minted by the sheet. */
      id?: string | undefined;
      /** Minted by the sheet, or the number printed on the card. */
      code?: string | undefined;
      valueCents?: number | undefined;
      itemId?: string | undefined;
      /** Which length of `itemId` (0054). The server prices the card from it. */
      optionId?: string | undefined;
      /** Who holds it, as a client of this book (0074). */
      recipientClientId?: string | undefined;
      /**
       * A free-text name, sent only when no client is named. Also the task
       * list's `person` either way.
       */
      recipientName?: string | undefined;
      purchaserClientId?: string | undefined;
      expiresOn?: string | null | undefined;
      /** One of the till's six. The Gift cards sheet never sends a card without one. */
      tender?: CheckoutInput['tender'] | undefined;
      /** What the owner called an `other`. */
      tenderLabel?: string | undefined;
      what: string;
    }): Promise<OutboxEntry> {
      return outbox.enqueue({
        kind: 'certificate.issue',
        payload: {
          ...(input.id === undefined ? {} : { id: input.id }),
          ...(input.code === undefined ? {} : { code: input.code }),
          ...(input.valueCents === undefined ? {} : { value_cents: input.valueCents }),
          ...(input.itemId === undefined ? {} : { item_id: input.itemId }),
          ...(input.optionId === undefined ? {} : { option_id: input.optionId }),
          ...(input.recipientClientId !== undefined
            ? { recipient_client_id: input.recipientClientId }
            : input.recipientName === undefined
              ? {}
              : { recipient_name: input.recipientName }),
          ...(input.purchaserClientId === undefined
            ? {}
            : { purchaser_client_id: input.purchaserClientId }),
          ...(input.expiresOn === undefined ? {} : { expires_on: input.expiresOn }),
          ...(input.tender === undefined ? {} : { tender: input.tender }),
          ...(input.tenderLabel === undefined ? {} : { tender_label: input.tenderLabel }),
        },
        describe: { what: input.what, person: input.recipientName },
      });
    },

    /**
     * Put credit on a guest's account.
     *
     * `kind` has no default, here as on the wire, because it is not a label — it
     * is the accounting. `cash` is money the guest handed over: income today, a
     * payment when spent. `gift` is goodwill: never income, a discount when
     * spent (§7.4 rule 2). A till that does not say which it is has not decided.
     */
    async addCredit(input: {
      clientId: string;
      name: string;
      kind: 'cash' | 'gift';
      amountCents: number;
      reason?: string | undefined;
    }): Promise<OutboxEntry> {
      return outbox.enqueue({
        kind: 'credit.add',
        target: { client_id: input.clientId },
        payload: {
          kind: input.kind,
          amount_cents: input.amountCents,
          ...(input.reason === undefined ? {} : { reason: input.reason }),
        },
        describe: {
          what:
            input.kind === 'cash'
              ? t('action.addCredit', { name: input.name })
              : t('action.giveCredit', { name: input.name }),
          person: input.name,
        },
      });
    },

    async createProduct(input: {
      name: string;
      priceCents: number;
      brand?: string | undefined;
    }): Promise<OutboxEntry> {
      return outbox.enqueue({
        kind: 'product.create',
        payload: {
          name: input.name,
          price_cents: input.priceCents,
          ...(input.brand === undefined ? {} : { brand: input.brand }),
        },
        describe: { what: t('action.addProduct', { name: input.name }) },
      });
    },

    /**
     * **The jar left the shelf** (C1 → B4), queued behind the sale that paid for
     * it.
     *
     * The Reservations screen moves a reservation *online*, because every other
     * transition on that screen is a race with other people and the buttons come
     * from the server. This one is different and is the whole reason the seam
     * exists: it happens at the counter, in the same tap as the settlement, and
     * it has to survive the same basement. So it is an intent, enqueued **after**
     * `checkout` — the outbox is a total order (spec §8.2), and "collected" that
     * landed without its sale beside it would be the wrong half arriving first.
     *
     * The body names a status and nothing else. `holdDays`, the deadline and the
     * legality of the move are all the server's, exactly as they are when a
     * finger presses the button on the other screen.
     */
    async collectReservation(input: {
      reservationId: string;
      clientName?: string | undefined;
    }): Promise<OutboxEntry> {
      const session = await mirror.session();
      return outbox.enqueue({
        kind: 'reservation.status',
        target: { reservation_id: input.reservationId },
        payload: {
          status: 'collected',
          ...(session === null ? {} : { tenant_id: session.tenant.tenant_id }),
        },
        describe: {
          what: t('action.collectReservation', {
            name: input.clientName ?? t('action.guest'),
          }),
          ...(input.clientName === undefined ? {} : { person: input.clientName }),
        },
      });
    },

    /**
     * **A stay, from the desk** (V5, verticals-plan S6) — through the same
     * `bookStay` the guest site and the partner use.
     *
     * The body names a room type, two dates and a party; it names **no rate**.
     * The sheet showed a total before the button, read from `GET /stays/quote`,
     * and that number stays on the screen: the server runs the same `quoteFor`
     * again when it writes the folio, and a device that could name the nightly
     * is a device that could name the wrong one (B7). There is no optimistic
     * row — the front desk is a `useRemote` screen, and the sheet awaits the
     * drain and re-reads the day.
     */
    async bookStay(input: {
      serviceId: string;
      roomName: string;
      checkIn: string;
      checkOut: string;
      partySize: number;
      notes?: string | undefined;
      source: 'staff' | 'walk_in';
      clientId?: string | undefined;
      guest?: { name: string; identities: WireIdentity[] } | undefined;
      clientName: string;
    }): Promise<OutboxEntry> {
      const session = await mirror.session();
      return outbox.enqueue({
        kind: 'stay.book',
        payload: {
          service_id: input.serviceId,
          check_in: input.checkIn,
          check_out: input.checkOut,
          party_size: input.partySize,
          source: input.source,
          ...(input.notes === undefined ? {} : { notes: input.notes }),
          ...(input.clientId === undefined ? {} : { client_id: input.clientId }),
          ...(input.guest === undefined ? {} : { guest: input.guest }),
          ...(session === null ? {} : { tenant_id: session.tenant.tenant_id }),
        },
        describe: {
          what: t('action.bookStay', { client: input.clientName, room: input.roomName }),
          person: input.clientName,
          whenIso: input.checkIn,
        },
      });
    },

    /**
     * **A seat, from the till** (V5, verticals-plan D6) — through the same
     * `bookDeparture` the guest site uses.
     *
     * Tier ids and seat counts, **never a fare**: the manifest sheet showed the
     * tiers priced and a total for the mix, and the server sums the same
     * catalog again when it sells the seats. `waiverAccepted` is the desk's
     * attestation that the guest in front of her acknowledged the document she
     * read them (owner's ruling 6); the server anchors it to the version it
     * publishes at that instant, and records nothing when it publishes none.
     */
    async sellSeat(input: {
      instanceId: string;
      tripName: string;
      whenIso: string;
      tickets: readonly { tierId: string; seats: number }[];
      notes?: string | undefined;
      source: 'staff' | 'walk_in';
      waiverAccepted?: boolean | undefined;
      clientId?: string | undefined;
      guest?: { name: string; identities: WireIdentity[] } | undefined;
      clientName: string;
    }): Promise<OutboxEntry> {
      const session = await mirror.session();
      return outbox.enqueue({
        kind: 'departure.book',
        target: { instance_id: input.instanceId },
        payload: {
          tickets: input.tickets.map((ticket) => ({ tier_id: ticket.tierId, seats: ticket.seats })),
          source: input.source,
          ...(input.waiverAccepted === true ? { waiver_accepted: true } : {}),
          ...(input.notes === undefined ? {} : { notes: input.notes }),
          ...(input.clientId === undefined ? {} : { client_id: input.clientId }),
          ...(input.guest === undefined ? {} : { guest: input.guest }),
          ...(session === null ? {} : { tenant_id: session.tenant.tenant_id }),
        },
        describe: {
          what: t('action.sellSeat', { client: input.clientName, trip: input.tripName }),
          person: input.clientName,
          whenIso: input.whenIso,
        },
      });
    },

    /** The diff, never the whole row — see `updateService` for the argument. */
    async updateProduct(input: {
      productId: string;
      name: string;
      changes: Record<string, unknown>;
    }): Promise<OutboxEntry> {
      return outbox.enqueue({
        kind: 'product.update',
        target: { product_id: input.productId },
        payload: input.changes,
        describe: { what: t('action.editProduct', { name: input.name }) },
      });
    },
  };
}

/**
 * A checkout, as the till describes it.
 *
 * Every optional field is optional in the same sense the wire's is: absent means
 * "the server decides". `useDeposit` with no `depositCents` is "whatever is left
 * of it, capped at what is owed" — which is the only honest thing this device
 * can say about a deposit whose amount `appointmentToWire` does not project.
 */
export interface CheckoutLine {
  itemId: string;
  qty: number;
  /**
   * Absent means the catalog price. Present is a deliberate override (§6.5).
   *
   * For an **open-price** item (B4) it is not an override and not optional: the
   * catalog has no price to fall back on, and the server refuses the line
   * without it. The screen always sends one for such a line.
   */
  unitPriceCents?: number | undefined;
  /** "coral scarf" — what a generic line actually was (B4). */
  note?: string | undefined;
  /**
   * **Which way the service on this line was sold** (migration 0048) — an
   * `item_options` id.
   *
   * It is not a price and it is not an override: it tells the server *which*
   * catalog number is this line's list, so the ninety-minute facial is listed
   * at $255 rather than at the default option's $85 and `discount = list −
   * charged` stays the honest sentence §7.4 rule 3 needs it to be.
   */
  optionId?: string | undefined;
  /**
   * **The service this line was attached to** (0048) — the parent's item id.
   *
   * Same job, one relationship over: an add-on's list price is what it costs
   * *on this parent*, which is generally less than what it costs standing
   * alone, and the link is the only place that number lives.
   */
  addOnOf?: string | undefined;
}

export interface CheckoutInput {
  lines: CheckoutLine[];
  /**
   * What settles the remainder — one of the six **recorded** tenders (B4).
   *
   * `RemainderKind` from `./wire`, spelled out here rather than imported so the
   * shape of an action's input stays readable at its own definition; the wire
   * schema is what a response is parsed against and this is what a screen hands
   * in. They are checked against each other by `tsc` at the one call site.
   */
  tender: 'cash' | 'check' | 'venmo' | 'paypal' | 'card' | 'other';
  /** The owner's own word for an `other` tender — "Zelle". `other` only. */
  tenderLabel?: string | undefined;
  /**
   * **A split payment** — "forty in cash and the rest on Venmo" (0046).
   *
   * Every part but the last names its amount; the last takes what is left, so
   * the server's `Σ tenders = total + tip` holds by construction and this
   * device never sends a total. Absent is the single-tender sale above.
   */
  tenders?:
    | readonly {
        kind: 'cash' | 'check' | 'venmo' | 'paypal' | 'card' | 'other';
        amountCents?: number | undefined;
        label?: string | undefined;
      }[]
    | undefined;
  /** Money collected for a person. Never revenue, never a discount (0046). */
  tipCents?: number | undefined;
  appointmentId?: string | undefined;
  clientId?: string | undefined;
  clientName?: string | undefined;
  taxCents?: number | undefined;
  discountCents?: number | undefined;
  certificateCode?: string | undefined;
  certificateCents?: number | undefined;
  useDeposit?: boolean | undefined;
  depositCents?: number | undefined;
  useCredit?: boolean | undefined;
  creditGiftCents?: number | undefined;
  creditCashCents?: number | undefined;
  note?: string | undefined;
}

export type Actions = ReturnType<typeof createActions>;

/**
 * The row a length verb is about, as an intent target. `option_id` is present
 * only when a length was named, so `intents.ts` can choose the door by its
 * presence and a broad intent's target is exactly the target it was before.
 */
function durationTarget(input: {
  clientId: string;
  serviceId: string;
  optionId?: string | undefined;
}): Record<string, string> {
  return {
    client_id: input.clientId,
    service_id: input.serviceId,
    ...(input.optionId === undefined ? {} : { option_id: input.optionId }),
  };
}
